import { useEffect, useRef, useState, useCallback } from "react";

export type Message = {
    id: string;
    role: "user" | "agent";
    text: string;
    timestamp: number;
};

export type ConnectionStatus = "idle" | "connecting" | "connected" | "disconnected" | "error";

export const useElevenLabsChat = () => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [status, setStatus] = useState<ConnectionStatus>("idle");
    const [isAgentTyping, setIsAgentTyping] = useState(false);
    const socketRef = useRef<WebSocket | null>(null);

    const connect = useCallback((token: string, dynamicVariables?: Record<string, string>) => {
        if (socketRef.current) {
            socketRef.current.close();
        }

        try {
            // Convert signed URL to WebSocket URL if it's not already
            const wsUrl = token.replace("https://", "wss://");

            console.log("Final WebSocket URL:", wsUrl);
            const ws = new WebSocket(wsUrl);
            socketRef.current = ws;

            setStatus("connecting");

            ws.onopen = () => {
                setStatus("connected");

                // Send initial configuration with dynamic variables if provided
                if (dynamicVariables) {
                    const initiationPayload = {
                        type: "conversation_initiation_client_data",
                        conversation_config_override: {
                            conversation: {
                                text_only: true,
                            },
                            agent: {
                                language: dynamicVariables.language,
                            },
                        },
                        dynamic_variables: { ...dynamicVariables, forceLanguage: "yes" },
                    };
                    console.log("Sending initiation payload:", initiationPayload);
                    ws.send(JSON.stringify(initiationPayload));
                }
            };

            ws.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);

                    if (data.type === "agent_chat_response_part") {
                        setIsAgentTyping(true);
                    }

                    if (data.type === "agent_response") {
                        const message: Message = {
                            id: Date.now().toString() + Math.random().toString(),
                            role: "agent",
                            text: data.agent_response_event?.agent_response || "",
                            timestamp: Date.now(),
                        };
                        setMessages((prev) => [...prev, message]);
                        setIsAgentTyping(false);
                    }

                    if (data.type === "agent_tool_response" && data.agent_tool_response?.tool_name === "end_call") {
                        socketRef.current?.close();
                    }
                } catch (error) {
                    console.error("Error parsing WebSocket message:", error);
                    setIsAgentTyping(false);
                }
            };

            ws.onerror = (error) => {
                console.error("WebSocket error:", error);
                setStatus("error");
                setIsAgentTyping(false);
            };

            ws.onclose = (event) => {
                console.log("WebSocket closed:", event.code, event.reason);
                setStatus("disconnected");
                setIsAgentTyping(false);
            };
        } catch (error) {
            console.error("Connection error:", error);
            setStatus("error");
            setIsAgentTyping(false);
        }
    }, []);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (socketRef.current) {
                socketRef.current.close();
            }
        };
    }, []);

    const sendMessage = useCallback((text: string) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            // Add user message to local state immediately for UI responsiveness
            const userMsg: Message = {
                id: Date.now().toString(),
                role: "user",
                text,
                timestamp: Date.now(),
            };
            setMessages((prev) => [...prev, userMsg]);
            setIsAgentTyping(true);

            // Send to ElevenLabs with the payload format provided by support
            const payload = {
                type: "user_message",
                text,
            };

            // I'll also log it
            console.log("Sending user message payload:", payload);
            socketRef.current.send(JSON.stringify(payload));
        }
    }, []);

    return {
        messages,
        status,
        sendMessage,
        connect,
        isAgentTyping,
    };
};
